import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import EodMiniCandleChart from '../components/eod/EodMiniCandleChart';
import ErrorBoundary from '../components/ErrorBoundary';
import TvLeftToolbar from '../components/tv/TvLeftToolbar';
import TvTopBar from '../components/tv/TvTopBar';
import TvRightPanel from '../components/tv/TvRightPanel';
import TvBottomTimeframes from '../components/tv/TvBottomTimeframes';
import { useLiveBoardOhlc, shouldUseLiveOhlc } from '../hooks/useLiveBoardOhlc';
import { useLiveBoardEod } from '../hooks/useLiveBoardEod';
import { useAuth } from '../hooks/useAuth';
import { useClock } from '../hooks/useClock';
import {
  calcFromDate,
  aggregateOHLCV,
  BOARD_TF_OPTIONS,
  isIntradayTf,
  intradayIntervalMinutes,
  intradayFetchMinutes,
  intradayHistoryDays,
  parseOhlcLiveResponse,
  aggregateIntradayOHLC,
} from '../utils/eodChartMath';
import {
  loadBoardSettings,
  saveBoardSettings,
  boardCacheKey,
  loadBoardCache,
  saveBoardCache,
  isValidTf,
} from '../utils/boardSettings';
import {
  assignSymbolsToPanes,
  layoutGridStyle,
  getDateRangeById,
  TV_DATE_RANGES,
  DEFAULT_LAYOUT_ID,
} from '../utils/chartLayouts';
import { todayISO } from '../utils/marketUtils';
import { resolveDataSource } from '../utils/dataSource';
import '../styles/eod-chart.css';
import '../styles/tv-layout.css';

const API = '/api/api_idx_chart.php';
const DEFAULT_EOD_PERIOD = '1M';

const BOARD_SETS = {
  NSE: {
    label: 'NSE Index',
    layout: 'nse',
    symbols: [
      { id: 'nifty', label: 'NIFTY', api: 'NIFTY', intradayApi: 'NIFTY', cell: 'main' },
      { id: 'banknifty', label: 'BANKNIFTY', api: 'BANKNIFTY', intradayApi: 'BANKNIFTY', cell: 'sub1' },
      { id: 'finnifty', label: 'FINNIFTY', api: 'FINNIFTY', intradayApi: 'FINNIFTY', cell: 'sub2' },
    ],
  },
  BSE: {
    label: 'BSE Index',
    layout: 'bse',
    symbols: [
      { id: 'sensex', label: 'SENSEX', api: 'BSE SENSEX', intradayApi: 'SENSEX', cell: 'left' },
      { id: 'bankex', label: 'BANKEX', api: 'BSE BANKEX', intradayApi: null, cell: 'right' },
    ],
  },
};

async function apiFetch(action, params = {}) {
  const url = `${API}?${new URLSearchParams({ action, ...params })}`;
  const res = await fetch(url, { credentials: 'include' });
  return res.json();
}

async function fetchIntraday(symbol, intervalMin = 5, tradeDate = todayISO(), daysOverride = null) {
  const days = daysOverride ?? intradayHistoryDays(intervalMin);
  const res = await fetch(
    `/api/api_ohlc_live.php?symbol=${encodeURIComponent(symbol)}&date=${encodeURIComponent(tradeDate)}&interval=${intervalMin}&live=1&days=${days}`,
    { credentials: 'include', cache: 'no-store' },
  );
  if (!res.ok) throw new Error(`${symbol}: intraday fetch failed (${res.status})`);
  const data = await res.json();
  const candles = parseOhlcLiveResponse(data, data.date || tradeDate);
  if (!candles.length) {
    if (days > 1) {
      const res2 = await fetch(
        `/api/api_ohlc_live.php?symbol=${encodeURIComponent(symbol)}&date=${encodeURIComponent(tradeDate)}&interval=${intervalMin}&live=1&days=1`,
        { credentials: 'include', cache: 'no-store' },
      );
      if (res2.ok) {
        const d2 = await res2.json();
        const c2 = parseOhlcLiveResponse(d2, d2.date || tradeDate);
        if (c2.length) return c2;
      }
    }
    throw new Error(`${symbol}: no intraday data (check feed / market hours)`);
  }
  return candles;
}

function initStateFromSettings(saved, urlExch) {
  return {
    exch: urlExch || saved.exch || 'NSE',
    tf: isValidTf(saved.tf) ? saved.tf : '5m',
    period: saved.period || DEFAULT_EOD_PERIOD,
    showFib: saved.showFib !== false,
    layoutId: saved.layoutId || DEFAULT_LAYOUT_ID,
    dateRangeId: saved.dateRangeId || '1M',
    rightPanelCollapsed: !!saved.rightPanelCollapsed,
  };
}

function lastBarStats(candles) {
  if (!candles?.length) return { last: null, chg: null };
  const last = candles[candles.length - 1];
  const prev = candles.length > 1 ? candles[candles.length - 2] : null;
  const close = Number(last.close);
  const prevClose = prev ? Number(prev.close) : Number(last.open);
  const chg = prevClose > 0 ? ((close - prevClose) / prevClose) * 100 : null;
  return { last: close, chg };
}

export default function EodIndexBoardPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const clock = useClock();
  const urlExch = searchParams.get('exch') === 'BSE' ? 'BSE' : searchParams.get('exch') === 'NSE' ? 'NSE' : null;
  const savedInit = useMemo(() => loadBoardSettings(), []);
  const init = useMemo(() => initStateFromSettings(savedInit, urlExch), [savedInit, urlExch]);

  const [exch, setExch] = useState(() => (init.exch === 'BSE' || init.exch === 'NSE' ? init.exch : 'NSE'));
  const [period, setPeriod] = useState(init.period || DEFAULT_EOD_PERIOD);
  const [tf, setTf] = useState(() => (isValidTf(init.tf) ? init.tf : '5m'));
  const [rawBySym, setRawBySym] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [liveSource, setLiveSource] = useState('');
  const [liveVia, setLiveVia] = useState('');
  const [liveStatus, setLiveStatus] = useState({ mode: 'idle', error: null, socketLive: false });
  const [showFib, setShowFib] = useState(init.showFib);
  const [layoutId, setLayoutId] = useState(init.layoutId || DEFAULT_LAYOUT_ID);
  const [dateRangeId, setDateRangeId] = useState(init.dateRangeId || '1M');
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(init.rightPanelCollapsed);
  const [activeTool, setActiveTool] = useState('cursor');
  const [focusSymbolId, setFocusSymbolId] = useState(null);
  const loadGenRef = useRef(0);
  const mountedRef = useRef(false);

  const chartSyncGroupId = `idx-board-${exch}`;

  const board = BOARD_SETS[exch] || BOARD_SETS.NSE;
  const intradayMode = isIntradayTf(tf);
  const tradeDate = todayISO();
  const fetchInterval = intradayFetchMinutes(tf);
  const liveActive = shouldUseLiveOhlc(intradayMode);
  const hasChartData = board.symbols.some((s) => (rawBySym[s.id] || []).length > 0);
  const dateRange = getDateRangeById(dateRangeId);

  const panes = useMemo(
    () => assignSymbolsToPanes(board.symbols, layoutId),
    [board.symbols, layoutId],
  );

  const focusSym = useMemo(() => {
    if (focusSymbolId) {
      const hit = board.symbols.find((s) => s.id === focusSymbolId);
      if (hit) return hit;
    }
    return board.symbols[0];
  }, [board.symbols, focusSymbolId]);

  const persistSettings = useCallback((patch) => {
    saveBoardSettings(patch);
  }, []);

  useEffect(() => {
    document.querySelector('.tmr-main')?.classList.add('ic-pg', 'tv-chart-mode');
    return () => document.querySelector('.tmr-main')?.classList.remove('ic-pg', 'tv-chart-mode');
  }, []);

  const viewBySym = useMemo(() => {
    const out = {};
    board.symbols.forEach((s) => {
      const raw = rawBySym[s.id] || [];
      if (intradayMode) {
        const isEodFallback = raw.length > 0 && typeof raw[0]?.date === 'string' && !String(raw[0].date).includes('T');
        out[s.id] = isEodFallback
          ? raw
          : aggregateIntradayOHLC(raw, intradayIntervalMinutes(tf), fetchInterval);
      } else {
        out[s.id] = aggregateOHLCV(raw, tf);
      }
    });
    return out;
  }, [rawBySym, tf, board.symbols, intradayMode, fetchInterval]);

  const focusStats = useMemo(
    () => lastBarStats(viewBySym[focusSym?.id] || []),
    [viewBySym, focusSym],
  );

  const loadBoard = useCallback(async (forceTf, opts = {}) => {
    const { clearData = false, silent = false } = opts;
    const gen = ++loadGenRef.current;
    const activeTf = forceTf ?? tf;
    const useIntraday = isIntradayTf(activeTf);
    const activeRange = getDateRangeById(dateRangeId);
    const eodPeriod = useIntraday ? period : (activeRange.eodPeriod || period || DEFAULT_EOD_PERIOD);
    const range = calcFromDate(eodPeriod);

    if (clearData) setRawBySym({});
    if (!silent) setIsRefreshing(true);
    setError(null);

    const histDays = useIntraday
      ? Math.min(30, Math.max(1, activeRange.intradayDays || intradayHistoryDays(intradayFetchMinutes(activeTf))))
      : 0;
    const cacheKey = boardCacheKey(
      exch,
      activeTf,
      useIntraday ? `${tradeDate}_d${histDays}` : range.from,
      useIntraday ? tradeDate : range.to,
    );
    let successCount = 0;
    const errors = [];

    const fetchSymbol = async (s) => {
      if (useIntraday) {
        if (s.intradayApi) {
          return fetchIntraday(s.intradayApi, intradayFetchMinutes(activeTf), tradeDate, histDays);
        }
        const res = await apiFetch('chartdata', { idx: s.api, from: tradeDate, to: tradeDate });
        if (res.error || !res.data?.length) {
          throw new Error(`${s.label}: no EOD fallback for today`);
        }
        return res.data;
      }
      const res = await apiFetch('chartdata', { idx: s.api, from: range.from, to: range.to });
      if (res.error) throw new Error(`${s.label}: ${res.error}`);
      if (!res.data?.length) throw new Error(`${s.label}: no data for this period`);
      return res.data;
    };

    await Promise.all(
      board.symbols.map(async (s) => {
        try {
          const data = await fetchSymbol(s);
          if (gen !== loadGenRef.current) return;
          successCount += 1;
          setRawBySym((prev) => {
            const next = { ...prev, [s.id]: data };
            saveBoardCache(cacheKey, next);
            return next;
          });
        } catch (e) {
          if (gen === loadGenRef.current) errors.push(e.message);
        }
      }),
    );

    if (gen !== loadGenRef.current) return;

    if (!successCount && errors.length) {
      setError(errors[0]);
      if (clearData) setRawBySym({});
    } else if (errors.length && successCount) {
      setError(null);
    }

    setIsRefreshing(false);
  }, [board.symbols, period, tf, exch, tradeDate, dateRangeId]);

  const onSourceChange = useCallback((src, meta = {}) => {
    if (typeof src === 'string' && src) setLiveSource(src);
    if (meta?.via) setLiveVia(meta.via);
    else if (typeof src === 'string' && (src === 'ltp' || src === 'ws' || src === 'db-poll')) {
      setLiveVia(src);
    }
  }, []);

  useLiveBoardOhlc({
    enabled: liveActive,
    symbols: board.symbols,
    tradeDate,
    intervalMinutes: fetchInterval,
    setRawBySym,
    onSourceChange,
    onStatusChange: setLiveStatus,
    seedIfEmpty: true,
  });

  useLiveBoardEod({
    enabled: !intradayMode && hasChartData,
    symbols: board.symbols,
    period: period || DEFAULT_EOD_PERIOD,
    setRawBySym,
    onStatusChange: setLiveStatus,
  });

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const useIntraday = isIntradayTf(init.tf);
    const range = calcFromDate(init.period || DEFAULT_EOD_PERIOD);
    const histDays = useIntraday ? intradayHistoryDays(intradayFetchMinutes(init.tf)) : 0;
    const cacheKey = boardCacheKey(
      init.exch,
      init.tf,
      useIntraday ? `${tradeDate}_d${histDays}` : range.from,
      useIntraday ? tradeDate : range.to,
    );
    const cached = loadBoardCache(cacheKey);
    const cacheUsable = cached && Object.values(cached).some((arr) => Array.isArray(arr) && arr.length > 0);
    if (cacheUsable) setRawBySym(cached);

    loadBoard(init.tf, { silent: !!cacheUsable, clearData: !cacheUsable });

    const t = setTimeout(() => {
      setRawBySym((prev) => {
        const has = Object.values(prev).some((arr) => Array.isArray(arr) && arr.length > 0);
        if (!has) loadBoard(init.tf, { clearData: false, silent: false });
        return prev;
      });
    }, 4000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onExchChange = (next) => {
    const safe = next === 'BSE' ? 'BSE' : 'NSE';
    setExch(safe);
    persistSettings({ exch: safe });
    setFocusSymbolId(null);
    setRawBySym({});
    setError(null);
    loadBoard(tf, { clearData: true });
  };

  const onDateRange = (id) => {
    setDateRangeId(id);
    const dr = getDateRangeById(id);
    const nextPeriod = dr.eodPeriod || period;
    setPeriod(nextPeriod);
    persistSettings({ dateRangeId: id, period: nextPeriod });
    loadBoard(tf, { clearData: true });
  };

  const onTfChange = (nextTf) => {
    setTf(nextTf);
    persistSettings({ tf: nextTf });
    loadBoard(nextTf, { clearData: true });
  };

  const onToggleFib = () => {
    setShowFib((v) => {
      const next = !v;
      persistSettings({ showFib: next });
      return next;
    });
  };

  const onLayoutChange = (id) => {
    setLayoutId(id);
    persistSettings({ layoutId: id });
  };

  const onToggleRight = () => {
    setRightCollapsed((v) => {
      const next = !v;
      persistSettings({ rightPanelCollapsed: next });
      return next;
    });
  };

  const dataSource = useMemo(() => resolveDataSource({
    source: liveSource,
    via: liveVia || liveStatus.via || '',
    mode: liveStatus.mode,
    socketLive: !!liveStatus.socketLive,
    intraday: intradayMode,
    lastSuccess: liveStatus.lastSuccess || null,
  }), [liveSource, liveVia, liveStatus, intradayMode]);

  const showBlockingLoad = isRefreshing && !hasChartData;
  const gridStyle = useMemo(() => layoutGridStyle(layoutId), [layoutId]);
  const clockLabel = [clock?.time, clock?.date].filter(Boolean).join(' · ') || '—';
  const sessionLabel = intradayMode ? 'NSE 09:15–15:30 IST' : `${BOARD_TF_OPTIONS.find((o) => o.value === tf)?.label || tf} · ${dateRangeId}`;

  return (
    <div className="eod-chart-page tv-workspace">
      <TvTopBar
        symbolLabel={focusSym?.label || 'NIFTY'}
        symbols={board.symbols}
        onSymbolChange={(label) => {
          const hit = board.symbols.find((s) => s.label === label);
          if (hit) setFocusSymbolId(hit.id);
        }}
        tf={tf}
        onTfChange={onTfChange}
        showFib={showFib}
        onToggleFib={onToggleFib}
        onRefresh={() => loadBoard(undefined, { clearData: false })}
        isRefreshing={isRefreshing}
        dataSourceLabel={dataSource?.label || dataSource?.short || ''}
        exch={exch}
        onExchChange={onExchChange}
      />

      <div className="tv-workspace-body">
        <TvLeftToolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
          showFib={showFib}
          onToggleFib={onToggleFib}
        />

        <div className="tv-chart-stage">
          {showBlockingLoad && (
            <div className="ic-loading"><div className="ic-spin" /> Loading index charts…</div>
          )}
          {!showBlockingLoad && error && !hasChartData && (
            <div className="ic-empty">
              <i className="bi bi-x-circle" style={{ fontSize: 18, color: 'var(--red)' }} /> {error}
            </div>
          )}
          {(hasChartData || (!showBlockingLoad && !error)) && (
            <div className="tv-multi-board" style={gridStyle}>
              {panes.map((pane) => {
                const s = pane.symbol;
                const data = viewBySym[s.id] || [];
                const isFocus = focusSym?.id === s.id;
                return (
                  <div
                    key={pane.key}
                    className={`tv-pane ic-board-cell${isFocus ? ' tv-pane-focus' : ''}`}
                    style={{ gridArea: pane.area }}
                    onMouseDown={() => setFocusSymbolId(s.id)}
                  >
                    <ErrorBoundary name={`${s.label} chart`}>
                      {data.length > 0 ? (
                        <EodMiniCandleChart
                          chartId={`${s.id}-${pane.paneIndex}`}
                          label={s.label + (intradayMode && !s.intradayApi ? ' (EOD)' : '')}
                          data={data}
                          fillHeight
                          intraday={intradayMode && !!s.intradayApi}
                          live={liveActive && !!s.intradayApi}
                          dataSource={s.intradayApi ? dataSource : resolveDataSource({ intraday: false })}
                          syncGroupId={chartSyncGroupId}
                          showFib={showFib}
                          tf={tf}
                          intervalMinutes={intradayMode ? intradayIntervalMinutes(tf) : 1}
                          user={user}
                        />
                      ) : (
                        <div className="ic-mini-wrap ic-mini-fill">
                          <div className="ic-mini-empty">
                            {isRefreshing
                              ? <><span className="ic-spin sm" /> Loading {s.label}…</>
                              : `No data for ${s.label}`}
                          </div>
                        </div>
                      )}
                    </ErrorBoundary>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <TvRightPanel
          layoutId={layoutId}
          onLayoutChange={onLayoutChange}
          layoutOpen={layoutOpen}
          setLayoutOpen={setLayoutOpen}
          symbolLabel={focusSym?.label || 'NIFTY'}
          lastPrice={focusStats.last}
          changePct={focusStats.chg}
          tf={tf}
          onTfChange={onTfChange}
          dateRangeId={dateRangeId}
          onDateRangeChange={onDateRange}
          dateRanges={TV_DATE_RANGES}
          crosshair={null}
          clockLabel={clockLabel}
          sessionLabel={sessionLabel}
          showFib={showFib}
          onToggleFib={onToggleFib}
          collapsed={rightCollapsed}
          onToggleCollapsed={onToggleRight}
        />
      </div>

      <TvBottomTimeframes
        value={dateRangeId}
        onChange={onDateRange}
        ranges={TV_DATE_RANGES}
      />
    </div>
  );
}
